import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { on as wsOn } from "@/lib/socket";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/ticket/StatusBadge";
import PriorityBadge from "@/components/ticket/PriorityBadge";
import SLACountdown from "@/components/ticket/SLACountdown";
import { formatIST, formatMoney } from "@/lib/format";
import { ArrowLeft, ImagePlus, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import MessageImage from "@/components/MessageImage";
import { readFileAsDataUrl } from "@/lib/image";

const ACTIONS = [
    { to: "IN_PROGRESS", label: "Start working" },
    { to: "WAITING_CLIENT", label: "Waiting for client" },
    { to: "RESOLVED", label: "Mark resolved" },
    { to: "CLOSED", label: "Close" },
];

export default function TicketDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [users, setUsers] = useState([]);
    const [replyText, setReplyText] = useState("");
    const [actionBusy, setActionBusy] = useState(false);
    const fileRef = useRef(null);

    const customerPhoneRef = useRef(null);
    customerPhoneRef.current = ticket?.customer_phone ?? null;

    const load = () => {
        api.get(`/tickets/${id}`).then((r) => setTicket(r.data));
        api.get(`/tickets/${id}/messages`).then((r) => setMessages(r.data));
        api.get(`/tickets/${id}/notes`).then((r) => setNotes(r.data));
    };

    useEffect(() => {
        load();
        if (user?.role === "ADMIN") {
            api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
        } else {
            setUsers([]);
        }
        const off1 = wsOn("ticket:updated", (d) => d.id === id && load());
        const off2 = wsOn("wa:outbound", (d) => {
            const ph = customerPhoneRef.current;
            if (ph && d.phone === ph) load();
        });
        const off3 = wsOn("wa:inbound", (d) => {
            const ph = customerPhoneRef.current;
            if (ph && d.phone === ph) load();
        });
        return () => {
            off1();
            off2();
            off3();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user?.role]);

    if (!ticket)
        return <div className="text-sm text-muted-foreground">Loading…</div>;

    const change = async (status) => {
        try {
            setActionBusy(true);
            await api.post(`/tickets/${id}/status`, { status });
            toast.success("Status updated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setActionBusy(false);
        }
    };
    const assign = async (uid) => {
        try {
            await api.post(`/tickets/${id}/assign`, { assigned_to: uid || null });
            toast.success(uid ? "Assignee updated" : "Ticket unassigned");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const addNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        try {
            await api.post(`/tickets/${id}/notes`, { body: newNote });
            setNewNote("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendReplyPayload = async (payload) => {
        try {
            await api.post("/whatsapp/inbox/send", {
                phone: ticket.customer_phone,
                ...payload,
            });
            toast.success("Sending to WhatsApp…");
            load();
            window.setTimeout(load, 2000);
            window.setTimeout(load, 6000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to send message");
            throw err;
        }
    };

    const sendReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;
        const body = replyText.trim();
        setReplyText("");
        try {
            await sendReplyPayload({ text: body });
        } catch {
            setReplyText(body);
        }
    };

    const onPickImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            const dataUrl = await readFileAsDataUrl(file);
            const caption = replyText.trim();
            setReplyText("");
            await sendReplyPayload({
                image_base64: dataUrl,
                mime_type: file.type,
                ...(caption ? { text: caption } : {}),
            });
        } catch (err) {
            toast.error(err?.message || "Failed to upload image");
        }
    };

    return (
        <div className="space-y-5" data-testid="ticket-detail-page">
            <Link
                to="/tickets"
                data-testid="ticket-detail-back"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="size-3.5" /> Back to tickets
            </Link>

            <div className="flex flex-wrap gap-3 items-start justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-mono">
                        Ticket
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight font-mono">
                        {ticket.ticket_number}
                    </h1>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <PriorityBadge value={ticket.priority_tier} />
                        <StatusBadge value={ticket.status} />
                        {ticket.escalation_level > 0 && (
                            <span className="text-[10px] uppercase tracking-wider text-red-400 border border-red-500/30 bg-red-500/10 px-2 py-0.5 rounded-sm">
                                L{ticket.escalation_level} Escalated
                            </span>
                        )}
                        {ticket.details_pending && (
                            <span className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 rounded-sm">
                                Details pending
                            </span>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                        SLA deadline
                    </div>
                    <div className="text-sm">{formatIST(ticket.sla_deadline)}</div>
                    <div className="mt-1">
                        <SLACountdown deadline={ticket.sla_deadline} status={ticket.status} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-card border border-border rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-3">
                        Conversation
                    </div>
                    <div className="wa-bg rounded-md p-4 max-h-[520px] overflow-y-auto space-y-2">
                        {messages.length === 0 && (
                            <div className="text-xs text-muted-foreground">No messages yet.</div>
                        )}
                        {messages.map((m) => (
                            <MsgBubble key={m.id} m={m} />
                        ))}
                    </div>
                    <form onSubmit={sendReply} className="mt-3 flex gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={onPickImage}
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="px-3 py-2 border border-border rounded-md text-sm hover:bg-secondary"
                            title="Send image"
                        >
                            <ImagePlus className="size-4" />
                        </button>
                        <input
                            data-testid="ticket-send-reply-input"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type a reply or caption…"
                            className="flex-1 bg-card border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
                        />
                        <button
                            data-testid="ticket-send-reply-btn"
                            className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm inline-flex items-center gap-1"
                        >
                            <Send className="size-3.5" /> Send
                        </button>
                    </form>
                </div>
                <div className="space-y-4">
                    <div className="bg-card border border-border rounded-md p-4 space-y-3">
                        <Info label="Customer" value={ticket.customer_name || "—"} />
                        <Info label="Phone" value={ticket.customer_phone} mono />
                        <Info label="Company" value={ticket.customer_company || "—"} />
                        <Info label="Category" value={ticket.category_name || "—"} />
                        <Info label="Created" value={formatIST(ticket.created_at)} />
                        <Info
                            label="Payment"
                            value={`${ticket.payment_status}${
                                ticket.amount != null && Number(ticket.amount) > 0
                                    ? ` · ${formatMoney(ticket.amount)}`
                                    : ""
                            }`}
                        />
                        <Info label="Issue" value={ticket.issue_description || "—"} block />
                    </div>

                    <div className="bg-card border border-border rounded-md p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-3">
                            Actions
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {ACTIONS.map((a) => (
                                <button
                                    key={a.to}
                                    data-testid={`ticket-action-${a.to}`}
                                    onClick={() => change(a.to)}
                                    disabled={actionBusy || ticket.status === a.to}
                                    className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-secondary disabled:opacity-40 transition-colors"
                                >
                                    {a.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-4">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                                Assignee
                            </label>
                            {user?.role === "ADMIN" ? (
                                <select
                                    data-testid="ticket-assignee-select"
                                    value={ticket.assigned_to || ""}
                                    onChange={(e) => assign(e.target.value)}
                                    className="mt-1 w-full bg-card border border-border rounded-md px-3 py-2 text-sm"
                                >
                                    <option value="">— Unassigned —</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name} ({u.role})
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div
                                    className="mt-1 w-full bg-card/50 border border-border rounded-md px-3 py-2 text-sm text-muted-foreground"
                                    data-testid="ticket-assignee-readonly"
                                >
                                    {ticket.assigned_to_name || "— Unassigned —"}
                                    <span className="block text-[10px] mt-1 opacity-70">
                                        Only admins can change assignee.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-md p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-3">
                            Internal notes
                        </div>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {notes.length === 0 && (
                                <div className="text-xs text-muted-foreground">No notes yet.</div>
                            )}
                            {notes.map((n) => (
                                <div
                                    key={n.id}
                                    className="border border-border rounded-md p-2 bg-[hsl(240,3%,6%)]"
                                >
                                    <div className="text-[10px] text-muted-foreground font-mono">
                                        {n.author_name} · {formatIST(n.created_at)}
                                    </div>
                                    <div className="text-sm">{n.body}</div>
                                </div>
                            ))}
                        </div>
                        <form onSubmit={addNote} className="mt-3 flex gap-2">
                            <input
                                data-testid="ticket-note-input"
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Add an internal note…"
                                className="flex-1 bg-card border border-border rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary"
                            />
                            <button
                                data-testid="ticket-note-submit"
                                className="px-3 py-2 text-xs bg-primary text-primary-foreground rounded-md"
                            >
                                Add
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Info({ label, value, mono, block }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {label}
            </div>
            <div className={`text-sm ${mono ? "font-mono" : ""} ${block ? "whitespace-pre-wrap" : ""}`}>
                {value}
            </div>
        </div>
    );
}

const MEDIA_ICONS = { image: "🖼", video: "🎥", audio: "🎵", voice: "🎙", document: "📄", sticker: "🎭" };

function MsgBubble({ m }) {
    const out = m.direction === "OUT";
    const body = m.body || "";
    const payload = m.payload || {};
    const isImage = m.kind === "image";
    const isMedia = ["video", "audio", "voice", "document", "sticker"].includes(m.kind);
    return (
        <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-md px-3 py-2 text-sm ${
                    out ? "wa-bubble-out" : "wa-bubble-in"
                }`}
            >
                <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1 font-mono">
                    {m.kind}
                </div>
                {isImage ? <MessageImage payload={payload} /> : null}
                {isMedia ? (
                    <div className="flex items-center gap-2 text-sm opacity-80 italic">
                        <span>{MEDIA_ICONS[m.kind] || "📎"}</span>
                        <span>{body}</span>
                        {payload.filename ? <span className="text-xs opacity-60">{payload.filename}</span> : null}
                    </div>
                ) : null}
                {!isImage && !isMedia && body ? (
                    <div className="whitespace-pre-wrap">{body}</div>
                ) : isImage && body && body !== "[Image]" ? (
                    <div className="whitespace-pre-wrap mt-2">{body}</div>
                ) : null}
                {m.kind === "interactive_list" && (
                    <div className="mt-2 space-y-1">
                        {(payload.sections || []).flatMap((s) => s.rows || []).map((r) => (
                            <div
                                key={r.id}
                                className="text-xs px-2 py-1 rounded bg-black/20 border border-white/10"
                            >
                                {r.title}
                            </div>
                        ))}
                    </div>
                )}
                {m.kind === "interactive_buttons" && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                        {(payload.buttons || []).map((b) => (
                            <div
                                key={b.id}
                                className="text-xs px-2 py-1 rounded bg-black/20 border border-white/10"
                            >
                                {b.title}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
